import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { CustomFieldsCard, useCenterPatientCustomValues, useCenterPatientDetail, useEraseCenterPatient, useSaveCenterPatientCustomValues, useUpdateStudent, academicYearOptions, CenterCourse, CenterPatientDetail, formatAcademicYear, StudentUpdate } from "../../../center";
import { studentChanges, studentFromDetail, validateStudent } from "../../domain/studentPerson";
import { countryName } from "../../../../shared/domain/countries";
import { CenterPatientIntakeProfile, PersonFields } from "../../../patient";
import { STUDENT_ERRORS } from "../refusals";

interface Props {
    centerId: string;
    /** The patient opened; the dialog is closed when null. */
    patientId: string | null;
    onClose: () => void;
    isTraining: boolean;
    courses: CenterCourse[];
    therapistName: (id: string) => string;
    /**
     * Every record the centre holds for this person, the opened one among
     * them: a student in two courses has one per course. With more than one,
     * the dialog switches between them.
     */
    records?: PersonRecord[];
}

export interface PersonRecord {
    patientId: string;
    therapistId: string;
    courseId?: string | null;
}

const SELECT_CLASS =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700";

/**
 * One patient of the centre, opened from the directory.
 *
 * Where they sit — their therapist, their groups, a school's course, how much
 * work there has been — and nothing of anybody's therapy. The centre, which
 * takes its people on itself, also sees and corrects their record: the fields
 * of a therapist's own patient form. A new therapist is a handover, done from
 * the list, because it moves the patient's sessions with them.
 */
export default function CenterPatientDetailModal({
    centerId,
    patientId,
    onClose,
    isTraining,
    courses,
    therapistName,
    records = [],
}: Props) {
    const { t, i18n } = useTranslation();
    // The record shown: the one opened, until another of the person's is chosen.
    const [activeId, setActiveId] = useState<string | null>(patientId);
    useEffect(() => {
        setActiveId(patientId);
        setEditing(false);
        setConfirmingErase(false);
        setEraseFailed(false);
    }, [patientId]);
    const { detail, loading, failed } = useCenterPatientDetail(centerId, activeId);
    const customValues = useCenterPatientCustomValues(centerId, activeId);
    const saveCustomValues = useSaveCenterPatientCustomValues(centerId, activeId);
    const [editing, setEditing] = useState(false);
    const erase = useEraseCenterPatient(centerId);
    const [confirmingErase, setConfirmingErase] = useState(false);
    const [eraseFailed, setEraseFailed] = useState(false);

    const recordLabel = (record: PersonRecord) => {
        if (!isTraining) return therapistName(record.therapistId);
        const recordCourse = courses.find((c) => c.id === record.courseId);
        return recordCourse ? recordCourse.internal_name : t("center.patients.detail.noCourse");
    };

    const close = () => {
        setEditing(false);
        onClose();
    };

    // Only the open record goes: a student in two courses keeps the other one.
    const handleErase = async () => {
        if (!detail) return;
        setEraseFailed(false);
        try {
            await erase.mutateAsync(detail.patient_id);
            setConfirmingErase(false);
            close();
        } catch {
            setConfirmingErase(false);
            setEraseFailed(true);
        }
    };

    const course = courses.find((c) => c.id === detail?.course_id);
    const day = (value: string | null) =>
        value
            ? new Date(value).toLocaleDateString(i18n?.language, { day: "numeric", month: "long", year: "numeric" })
            : "—";

    const birthDay = (value?: string | null) =>
        value ? new Date(`${value}T00:00:00`).toLocaleDateString(i18n?.language) : "";
    const sexLabel = (value?: string | null) =>
        value ? t({ male: "patient.sexMale", female: "patient.sexFemale" }[value] ?? "patient.sexOther") : "";
    const pronounLabel = (value?: string | null) => {
        const keys: Record<string, string> = {
            "he/him": "patient.pronounsHeHim",
            "she/her": "patient.pronounsSheHer",
            "they/them": "patient.pronounsTheyThem",
        };
        return value ? (keys[value] ? t(keys[value]) : value) : "";
    };

    // The record in the order an administrator looks for it: how to reach
    // the person, who they are, and where they sit in the centre.
    const sections: { title: string; rows: [string, string][] }[] = detail
        ? [
              {
                  title: t("center.patients.detail.sections.contact"),
                  rows: [
                      [t("center.patients.detail.email"), detail.contact_email || ""],
                      ...(detail.profile
                          ? ([
                                [t("patient.phone"), detail.profile.contact_phone || ""],
                                [
                                    t("patient.whatsapp"),
                                    [
                                        detail.profile.whatsapp_phone,
                                        detail.profile.whatsapp_phone && detail.profile.whatsapp_opt_in === false
                                            ? t("patient.whatsappOff")
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(" · "),
                                ],
                                [
                                    t("patient.address"),
                                    [
                                        detail.profile.street,
                                        detail.profile.postal_code,
                                        detail.profile.city,
                                        detail.profile.state,
                                        detail.profile.country ? countryName(detail.profile.country, i18n?.language) : null,
                                    ]
                                        .filter(Boolean)
                                        .join(", "),
                                ],
                                [t("patient.timezone"), detail.profile.timezone || ""],
                            ] as [string, string][])
                          : []),
                  ],
              },
              ...(detail.profile
                  ? [
                        {
                            title: t("center.patients.detail.sections.personal"),
                            rows: [
                                [t("patient.pronouns"), pronounLabel(detail.profile.pronouns)],
                                [t("patient.dateOfBirth"), birthDay(detail.profile.date_of_birth)],
                                [t("patient.sex"), sexLabel(detail.profile.sex)],
                            ] as [string, string][],
                        },
                    ]
                  : []),
              {
                  title: t("center.patients.detail.sections.center"),
                  rows: [
                      [t("center.patients.therapist"), therapistName(detail.therapist_id)],
                      ...(isTraining
                          ? ([
                                [t("patient.course.course"), course ? course.internal_name : ""],
                                [t("center.courses.publicName"), course ? course.name : ""],
                                [
                                    t("patient.course.year"),
                                    detail.academic_year ? formatAcademicYear(detail.academic_year) : "",
                                ],
                            ] as [string, string][])
                          : []),
                      [
                          t("center.patients.detail.groups"),
                          detail.groups.map((group) => group.name || t("center.patients.detail.unnamedGroup")).join(", "),
                      ],
                  ],
              },
          ]
        : [];

    const stats: [string, string][] = detail
        ? [
              [t("center.patients.sessions"), String(detail.session_count)],
              [t("center.patients.lastSession"), day(detail.last_session_at)],
              [t("center.patients.detail.inCenterSince"), day(detail.in_center_since)],
          ]
        : [];

    return (
        <Modal
            isOpen={Boolean(patientId)}
            onClose={close}
            ariaLabel={t("center.patients.detail.title")}
            className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            {records.length > 1 && !editing && (
                <div
                    role="tablist"
                    aria-label={t(isTraining ? "center.patients.detail.recordsTraining" : "center.patients.detail.records")}
                    className="mb-5 mr-14 flex flex-wrap gap-2"
                >
                    {records.map((record) => {
                        const selected = record.patientId === activeId;
                        return (
                            <button
                                key={record.patientId}
                                type="button"
                                role="tab"
                                aria-selected={selected}
                                onClick={() => setActiveId(record.patientId)}
                                className={`rounded-full border px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                                    selected
                                        ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                        : "border-gray-200 text-content-secondary hover:border-gray-300 dark:border-gray-700"
                                }`}
                            >
                                {recordLabel(record)}
                            </button>
                        );
                    })}
                </div>
            )}
            {loading ? (
                <p className="text-sm text-gray-500">{t("common.loading")}</p>
            ) : failed || !detail ? (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("center.patients.detail.error")}
                </p>
            ) : editing ? (
                <StudentEditForm
                    centerId={centerId}
                    detail={detail}
                    courses={courses}
                    isTraining={isTraining}
                    onDone={() => setEditing(false)}
                />
            ) : (
                <>
                    {/* Room on the right for the dialog's own close button, which sits over this corner. */}
                    <div className="flex flex-wrap items-start justify-between gap-4 pr-14">
                        <div className="flex min-w-0 items-center gap-3">
                            <span
                                aria-hidden="true"
                                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-50 text-base font-semibold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                            >
                                {`${detail.first_name.charAt(0)}${detail.last_name.charAt(0)}`.toUpperCase()}
                            </span>
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                                    {t("center.patients.detail.title")}
                                </p>
                                <h3 className="truncate text-lg font-semibold text-content-primary">
                                    {detail.first_name} {detail.last_name}
                                </h3>
                                <p className="truncate text-sm text-content-secondary">{therapistName(detail.therapist_id)}</p>
                            </div>
                        </div>
                        <div className="flex shrink-0 gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditing(true)}
                                startIcon={<Pencil size={14} aria-hidden="true" />}
                            >
                                {t("center.patients.detail.edit")}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmingErase(true)}
                                startIcon={<Trash2 size={14} aria-hidden="true" />}
                                className="text-red-600 hover:text-red-700 dark:text-red-400"
                            >
                                {t(isTraining ? "center.patients.detail.deleteStudent" : "center.patients.detail.deletePatient")}
                            </Button>
                        </div>
                    </div>
                    {eraseFailed && (
                        <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                            {t("center.patients.detail.deleteError")}
                        </p>
                    )}

                    <dl className="mt-5 grid grid-cols-3 gap-2 sm:gap-3">
                        {stats.map(([label, value]) => (
                            <div key={label} className="rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-white/[0.03]">
                                <dt className="text-xs text-content-tertiary">{label}</dt>
                                <dd className="mt-0.5 text-sm font-semibold text-content-primary">{value}</dd>
                            </div>
                        ))}
                    </dl>

                    {sections.map((section) => (
                        <section key={section.title} className="mt-6">
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-content-tertiary">
                                {section.title}
                            </h4>
                            <dl className="mt-2 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                                {section.rows.map(([label, value]) => (
                                    <div key={label} className="min-w-0">
                                        <dt className="text-xs text-content-secondary">{label}</dt>
                                        <dd className={`break-words text-sm ${value ? "text-content-primary" : "text-content-tertiary"}`}>
                                            {value || "—"}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    ))}

                    {/* A training centre's students have no intake profile. */}
                    {!isTraining && (
                        <CenterPatientIntakeProfile
                            className="mt-6"
                            centerId={centerId}
                            patientId={detail.patient_id}
                            dateOfBirth={detail.profile?.date_of_birth}
                        />
                    )}
                    <CustomFieldsCard
                        className="mt-4"
                        headingLevel="h4"
                        data={customValues.data}
                        isLoading={customValues.loading}
                        isError={customValues.failed}
                        onRetry={customValues.retry}
                        onSave={(values) => saveCustomValues.mutateAsync(values)}
                        isSaving={saveCustomValues.isPending}
                    />
                    <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">{t("center.patients.detail.privacy")}</p>
                    <ConfirmDialog
                        isOpen={confirmingErase}
                        destructive
                        isBusy={erase.isPending}
                        title={t(isTraining ? "center.patients.detail.deleteStudentTitle" : "center.patients.detail.deletePatientTitle", {
                            name: `${detail.first_name} ${detail.last_name}`,
                        })}
                        message={t("center.patients.detail.deleteConfirm")}
                        confirmLabel={t("center.patients.detail.deleteConfirmAction")}
                        cancelLabel={t("common.cancel")}
                        onConfirm={() => void handleErase()}
                        onCancel={() => setConfirmingErase(false)}
                    />
                </>
            )}
        </Modal>
    );
}

/** Correcting a student: the patient form's fields, and only what changed is sent. */
function StudentEditForm({
    centerId,
    detail,
    courses,
    isTraining,
    onDone,
}: {
    centerId: string;
    detail: CenterPatientDetail;
    courses: CenterCourse[];
    /** Only a school has a course and a year to change. */
    isTraining: boolean;
    onDone: () => void;
}) {
    const { t } = useTranslation();
    const update = useUpdateStudent(centerId);
    const [original] = useState(() => studentFromDetail(detail));
    const [person, setPerson] = useState(original);
    const [courseId, setCourseId] = useState(detail.course_id ?? "");
    const [year, setYear] = useState(detail.academic_year ? String(detail.academic_year) : "");
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const personErrors = validateStudent(person);
    const invalid = Object.keys(personErrors).length > 0 || (isTraining && !courseId);

    const changes = (): StudentUpdate => {
        const next: StudentUpdate = studentChanges(original, person);
        if (!isTraining) return next;
        if (courseId !== (detail.course_id ?? "")) next.course_id = courseId;
        const nextYear = year ? Number(year) : null;
        if (nextYear !== (detail.academic_year ?? null)) next.academic_year = nextYear;
        return next;
    };

    const handleSave = async () => {
        setSubmitted(true);
        setError(null);
        if (invalid) return;
        const next = changes();
        if (Object.keys(next).length === 0) {
            onDone();
            return;
        }
        try {
            await update.mutateAsync({ patientId: detail.patient_id, changes: next });
            onDone();
        } catch (err: unknown) {
            const code = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setError(t(STUDENT_ERRORS[code ?? ""] ?? "center.students.errors.generic"));
        }
    };

    return (
        <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={(e) => {
                e.preventDefault();
                void handleSave();
            }}
        >
            <h3 className="text-lg font-semibold text-content-primary">{t("center.patients.detail.editTitle")}</h3>
            <PersonFields
                idPrefix="student-edit"
                value={person}
                onChange={(changes) => setPerson((current) => ({ ...current, ...changes }))}
                errors={submitted ? personErrors : {}}
                refusedWhatsApp={
                    Boolean(detail?.profile?.whatsapp_phone) && detail?.profile?.whatsapp_opt_in === false
                }
            />
            {isTraining && (
                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="student-edit-course">
                            {t("patient.course.course")}
                            <span className="text-red-500"> *</span>
                        </Label>
                        <select
                            id="student-edit-course"
                            value={courseId}
                            onChange={(e) => setCourseId(e.target.value)}
                            aria-invalid={submitted && !courseId}
                            className={SELECT_CLASS}
                        >
                            <option value="">{t("center.students.chooseCourse")}</option>
                            {courses.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.internal_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label htmlFor="student-edit-year">{t("patient.course.year")}</Label>
                        <select
                            id="student-edit-year"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            className={SELECT_CLASS}
                        >
                            <option value="">{t("patient.course.noYear")}</option>
                            {academicYearOptions(new Date(), detail.academic_year).map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">{t("center.patients.detail.mentorHint")}</p>

            {submitted && invalid && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("center.patients.detail.errorForm")}
                </p>
            )}
            {error && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onDone}>
                    {t("common.cancel")}
                </Button>
                <Button type="submit" loading={update.isPending} disabled={update.isPending}>
                    {t("common.save")}
                </Button>
            </div>
        </form>
    );
}
